// containers/restaurant/RestaurantPage.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "@/config/env";
import { Link } from "react-router-dom";
import { getCategories } from "@/services/api/categoryService";
import { toast } from "react-hot-toast";
import { getProducts } from "@/services/api/productService";
import { addToCart } from "@/services/api/cartService";

// Imagenes
import insideRestoImage from "@/assets/images/inside-resto.png";
import restoLogoGrande from "@/assets/images/resto-logo-grande.png";
import restoLogoPequeno from "@/assets/images/resto-logo-pequeño.png";
import techLogo from "@/assets/images/tech-logo.png";

// Components
import SearchSection from "@/components/ui/search-section";
import MenuCategories from "@/components/ui/menu-categories";
import CategoriesSection from "@/components/ui/categories-section";
import ProductList from "@/components/ui/product-list";

const RestaurantPage = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const idResto = queryParams.get("param1");
  const idMesa = queryParams.get("param2");
  const [nameTable, setNameTable] = useState("");
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [categories, setCategories] = useState({
    drinks: [],
    kitchen: [],
  });
  const [allProducts, setAllProducts] = useState([]); // Nuevo estado para almacenar todos los productos
  const [displayProducts, setDisplayProducts] = useState([]); // Productos a mostrar (filtrados o no)
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAddons, setSelectedAddons] = useState({});
  const [loadingProducts, setLoadingProducts] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    const initializeData = async () => {
      await getTableDataInformation();
      const currentMesaId = localStorage.getItem("mesaId");
      if (!currentMesaId && idMesa) {
        localStorage.setItem("mesaId", idMesa);
      }
    };

    initializeData();
  }, [idMesa]);

  // Efecto para manejar la inicialización y cambios de mesa
  useEffect(() => {
    const initializeData = async () => {
      // Verificar cambio de mesa
      const currentMesaId = localStorage.getItem("mesaId");
      if (idMesa && currentMesaId !== idMesa) {
        // Limpiar localStorage
        localStorage.clear();
        localStorage.setItem("mesaId", idMesa);

        // Reiniciar estados
        setDisplayProducts([]);
        setAllProducts([]);
        setSelectedAddons({});
        setSelectedMainCategory(null);
        setSelectedSubCategory(null);
        setSearchQuery("");
      }

      // Obtener información de la mesa
      await getTableDataInformation();
    };

    initializeData();
  }, [idMesa]);

  const getTableDataInformation = async () => {
    try {
      if (!localStorage.getItem("mesaId")) {
        throw new Error("No se proporcionó ID de mesa");
      }

      const response = await axios.post(`${API_URL}/api/table/getTable`, {
        mesaId: localStorage.getItem("mesaId"),
      });

      if (!response.data || !response.data.table) {
        throw new Error("Datos de mesa inválidos");
      }

      // Guardar la información completa de la mesa
      const tableInfo = {
        ...response.data.table,
        mesaId: localStorage.getItem("mesaId"),
        restoId: localStorage.getItem("restoId"),
      };

      localStorage.setItem("tableInfo", JSON.stringify(tableInfo));
      localStorage.setItem("mesaId", idMesa);
      localStorage.setItem("redirectVirtual", response.data.session_url);

      setNameTable(response.data.table.descriptionMesa);
    } catch (error) {
      console.error("Error fetching table information:", error);
     // toast.error("Error al obtener información de la mesa");
    }
  };

  // Cargar categorías
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data);
      } catch (err) {
        console.error("Error fetching categories:", err);
        toast.error("Error al cargar las categorías");
      }
    };

    fetchCategories();
  }, []);

  // Cargar todos los productos inicialmente
  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        setLoading(true);
        const response = await getProducts();
        if (Array.isArray(response)) {
          const productsWithQuantity = response.map((product) => ({
            ...product,
            quantity: 0,
            id: product._id || product.id,
          }));
          setAllProducts(productsWithQuantity);
        }
      } catch (error) {
        console.error("Error fetching all products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllProducts();
  }, []);

  // Efecto para filtrar productos según categoría y búsqueda
  useEffect(() => {
    // Si no hay búsqueda ni categoría seleccionada, no mostrar productos
    if (!searchQuery.trim() && !selectedSubCategory) {
      setDisplayProducts([]);
      return;
    }

    let filteredProducts = [...allProducts];

    // Si hay término de búsqueda, filtrar por búsqueda
    if (searchQuery.trim()) {
      const searchTerm = searchQuery.toLowerCase().trim();
      filteredProducts = filteredProducts.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm) ||
          product.description?.toLowerCase().includes(searchTerm)
      );
    }
    // Si hay categoría seleccionada, filtrar por categoría
    else if (selectedSubCategory) {
      filteredProducts = filteredProducts.filter(
        (product) =>
          product.category.toLowerCase() ===
          selectedSubCategory.name.toLowerCase()
      );
    }

    setDisplayProducts(filteredProducts);
  }, [selectedSubCategory, searchQuery, allProducts]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    // Limpiar selección de categoría cuando se busca
    if (query.trim()) {
      setSelectedMainCategory(null);
      setSelectedSubCategory(null);
    }
  };

  const handleUpdateQuantity = async (productId, action) => {
    console.log("handleUpdateQuantity ")
    try {
      const product = displayProducts.find((p) => p.id === productId);
      if (!product) {
        console.error("[RestaurantPage] Product not found:", productId);
        return;
      }

      // Activar loading para este producto específico
      setLoadingProducts((prev) => ({
        ...prev,
        [productId]: true,
      }));

      if (action === "increase") {
        const hasAddons = product.addOn?.length > 1;
        const selectedAddon = hasAddons ? selectedAddons[productId] : null;

        if (hasAddons && !selectedAddon) {
          toast.error("Por favor selecciona una opción antes de agregar");
          return;
        }

        try {
          await addToCart(productId, selectedAddon);
          toast.success("Item agregado a su orden");

          setDisplayProducts((currentProducts) =>
            currentProducts.map((p) =>
              p.id === productId
                ? {
                    ...p,
                    quantity: p.quantity + 1,
                    selectedOption: selectedAddon,
                  }
                : p
            )
          );
        } catch (error) {
          console.error("[RestaurantPage] Error adding to cart:", error);
          toast.error("Error al agregar al carrito");
        }
      } else {
        setDisplayProducts((currentProducts) =>
          currentProducts.map((p) =>
            p.id === productId
              ? { ...p, quantity: Math.max(0, p.quantity - 1) }
              : p
          )
        );
      }
    } catch (error) {
      console.error("[RestaurantPage] Error updating quantity:", error);
      toast.error("Error al actualizar la cantidad");
    } finally {
      // Desactivar loading para este producto
      setLoadingProducts((prev) => ({
        ...prev,
        [productId]: false,
      }));
    }
  };

  const handleAddonSelect = (productId, option) => {
    setSelectedAddons((prev) => ({
      ...prev,
      [productId]: option,
    }));
  };

  const handleMainCategorySelect = (category) => {
    setSelectedMainCategory(category);
    setSelectedSubCategory(null);
    setSearchQuery(""); // Limpiar búsqueda al seleccionar categoría
  };

  const handleSubCategorySelect = (categoryId) => {
    const selectedCategory = categories[
      selectedMainCategory === "comidas" ? "kitchen" : "drinks"
    ].find((cat) => cat._id === categoryId);

    if (!selectedCategory) {
      console.error("[RestaurantPage] Category not found for id:", categoryId);
      return;
    }

    setSelectedSubCategory({
      id: categoryId,
      name: selectedCategory.name,
    });
    setSearchQuery(""); // Limpiar búsqueda al seleccionar subcategoría
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md p-4 flex justify-between items-center">
        <Link to="/" className="flex-shrink-0">
          <img src={restoLogoPequeno} alt="Tataowi" className="h-6 w-auto" />
        </Link>
        <nav className="flex gap-4">
          <Link
            to="/pedidos"
            className="text-gray-800 hover:text-gray-600 font-medium text-sm"
          >
            MIS PEDIDOS
          </Link>
          <Link
            to="/pagar"
            className="text-gray-800 hover:text-gray-600 font-medium text-sm"
          >
            PAGAR
          </Link>
        </nav>
      </header>

      <div className="h-[60px]"></div>

      {/* Hero Section */}
      <section className="relative w-full h-48 sm:h-64 md:h-96">
        <img
          src={insideRestoImage}
          alt="Restaurant Interior"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
          <img
            src={restoLogoGrande}
            alt="Tataowi"
            className="w-48 sm:w-64 h-auto"
          />
        </div>
      </section>

      <div className="text-center py-6">
        <h1 className="text-2xl font-bold text-gray-800">Mesa {nameTable}</h1>
      </div>

      <SearchSection onSearch={handleSearch} />

      <MenuCategories
        selectedMainCategory={selectedMainCategory}
        onMainCategorySelect={handleMainCategorySelect}
      />

      <CategoriesSection
        selectedMainCategory={selectedMainCategory}
        categories={categories}
        onSelectSubcategory={handleSubCategorySelect}
        selectedSubCategory={selectedSubCategory?.id}
      />

      {/* Sección de Productos */}
      {!searchQuery.trim() && !selectedSubCategory ? (
        <div className="px-4 py-8 max-w-4xl mx-auto w-full">
          <div className="text-center space-y-4">
            <p className="text-gray-600 text-lg">Explora nuestro menú</p>
            <p className="text-gray-500">
              Busca un producto específico o navega por nuestras categorías de
              Comidas y Bebidas
            </p>
            {/* Botones de categorías principales */}
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={() => handleMainCategorySelect("comidas")}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Ver Comidas
              </button>
              <button
                onClick={() => handleMainCategorySelect("bebidas")}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Ver Bebidas
              </button>
            </div>
          </div>
        </div>
      ) : searchQuery.trim() && displayProducts.length === 0 ? (
        <div className="px-4 py-8 max-w-4xl mx-auto w-full">
          <div className="text-center space-y-4">
            <p className="text-gray-600 text-lg">
              No se encontraron productos para tu búsqueda
            </p>
            {/* ... resto del código igual ... */}
          </div>
        </div>
      ) : (
        <ProductList
          products={displayProducts}
          loading={loading}
          onUpdateQuantity={handleUpdateQuantity}
          selectedAddons={selectedAddons}
          onAddonSelect={handleAddonSelect}
          loadingProducts={loadingProducts}
        />
      )}

      {/* Footer */}
      <footer className="mt-auto py-4 text-center text-gray-600 text-sm">
        <p>Con la tecnología</p>
        <div className="w-20 mx-auto">
          <img
            src={techLogo}
            alt="restoOrder"
            className="w-full h-auto object-contain"
          />
        </div>
      </footer>
    </div>
  );
};

export default RestaurantPage;
